export default function GenerationMeter() {
  const generations = [
    {
      id: 1,
      generation: '1세대',
      name: '기계식 수도미터기',
      period: '1900년대 ~ 2010년대',
      image:
        'https://public.readdy.ai/ai/img_res/8a35efb2e746de2c39fa7fc295fbeae1.jpg',
      features: [
        '회전식 기계 구조',
        '아날로그 눈금 표시',
        '정기적인 검침원 방문 필요',
        '부정사용 취약',
      ],
      limitations: [
        '원격 검침 불가',
        '자석으로 조작 가능',
        '정확도 저하',
        '유지보수 어려움',
      ],
      color: 'from-gray-600 to-gray-800',
    },
    {
      id: 2,
      generation: '2세대',
      name: '전자식 수도미터기',
      period: '2010년대 ~ 2019년',
      image:
        'https://readdy.ai/api/search-image?query=modern%20electronic%20digital%20water%20meter%20with%20LCD%20display%20screen%20showing%20numbers%20clear%20readable%20interface%20industrial%20design%20plastic%20and%20metal%20construction%20digital%20readout%20interface%20product%20photography%20on%20white%20background%20utility%20metering%20device&width=600&height=600&seq=meter-gen2-002&orientation=squarish',
      features: [
        '디지털 디스플레이',
        '전자식 계량',
        '데이터 저장 기능',
        '배터리 구동',
      ],
      limitations: [
        '배터리 교체 필요',
        '통신 기능 제한적',
        '여전히 납 성분 사용',
        '실시간 모니터링 불가',
      ],
      color: 'from-teal-600 to-teal-800',
    },
    {
      id: 3,
      generation: '3세대',
      name: '광센서 스마트 수도미터',
      period: '2019년 ~ 현재',
      image:
        'https://static.readdy.ai/image/2299103fa86076f2ed06076e2d6cbff8/bfdb5fdf9dfa93d24bae6d4fecfdcc62.png',
      features: [
        '광학 센싱 기술',
        'IoT 실시간 연동',
        '스마트폰 모니터링',
        '스테인리스 재질',
      ],
      advantages: [
        '납 성분 완전 제거',
        '부정사용 원천 차단',
        '원격 검침 가능',
        '실시간 데이터 분석',
      ],
      color: 'from-emerald-600 to-teal-700',
      highlight: true,
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-20">
          <div className="inline-block mb-4 px-4 md:px-6 py-2 bg-teal-50 border border-teal-200 rounded-full">
            <span className="text-teal-600 font-semibold tracking-wider text-xs md:text-sm uppercase">
              세대별 진화
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 mb-4 md:mb-6">
            수도미터기의{' '}
            <span className="text-teal-600">세대별 발전</span>
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            기계식에서 광센서 스마트 디지털까지, 수도미터기 기술의 혁신적 변화
          </p>
        </div>

        {/* Timeline */}
        <div className="relative mb-12 md:mb-20">
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-gray-300 via-teal-300 to-emerald-500 transform -translate-y-1/2 hidden lg:block"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 relative">
            {generations.map((gen) => (
              <div key={gen.id} className="relative">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-white border-4 border-teal-600 rounded-full z-10 hidden lg:block"></div>

                <div
                  className={`relative bg-white rounded-2xl md:rounded-3xl shadow-xl overflow-hidden border-2 ${
                    gen.highlight ? 'border-teal-500' : 'border-gray-200'
                  } hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 mt-0 lg:mt-8`}
                >
                  {gen.highlight && (
                    <div className="absolute top-3 md:top-4 right-3 md:right-4 z-10">
                      <div className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-bold shadow-lg">
                        최신 기술
                      </div>
                    </div>
                  )}

                  <div className={`bg-gradient-to-r ${gen.color} text-white py-3 md:py-4 px-4 md:px-6`}>
                    <div className="text-xs md:text-sm font-semibold opacity-90">
                      {gen.generation}
                    </div>
                    <div className="text-base md:text-xl font-black">{gen.name}</div>
                    <div className="text-[10px] md:text-xs opacity-80 mt-1">{gen.period}</div>
                  </div>

                  <div
                    className={`relative h-40 sm:h-48 md:h-64 w-full flex items-center justify-center p-3 sm:p-4 md:p-8 ${
                      gen.highlight ? 'bg-gradient-to-br from-teal-50 to-white' : 'bg-gray-50'
                    }`}
                  >
                    <img
                      src={gen.image}
                      alt={gen.name}
                      title={`${gen.name} - 로보에셋그리드`}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  <div className="p-4 md:p-6">
                    <h4 className="font-bold text-sm md:text-base text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-teal-600"></i>
                      주요 특징
                    </h4>
                    <ul className="space-y-1.5 md:space-y-2 mb-4 md:mb-6">
                      {gen.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2 text-xs md:text-sm text-gray-700"
                        >
                          <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {gen.limitations && (
                      <>
                        <h4 className="font-bold text-sm md:text-base text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
                          <i className="ri-close-circle-line text-red-600"></i>
                          한계점
                        </h4>
                        <ul className="space-y-1.5 md:space-y-2">
                          {gen.limitations.map((limitation, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-2 text-xs md:text-sm text-gray-600"
                            >
                              <i className="ri-arrow-right-s-line text-red-600 mt-0.5 flex-shrink-0"></i>
                              <span>{limitation}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {gen.advantages && (
                      <>
                        <h4 className="font-bold text-sm md:text-base text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
                          <i className="ri-star-line text-teal-600"></i>
                          핵심 장점
                        </h4>
                        <ul className="space-y-1.5 md:space-y-2">
                          {gen.advantages.map((advantage, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-2 text-xs md:text-sm text-teal-700 font-medium"
                            >
                              <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                              <span>{advantage}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>

                  <div className={`h-2 bg-gradient-to-r ${gen.color}`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl md:rounded-3xl p-4 md:p-8 shadow-lg border border-teal-200">
          <h3 className="text-xl md:text-3xl font-black text-gray-900 mb-6 md:mb-8 text-center">
            세대별 비교
          </h3>

          <div className="overflow-x-auto -mx-2 md:mx-0">
            <table className="w-full min-w-[400px]">
              <thead>
                <tr className="border-b-2 border-teal-300">
                  <th className="text-left py-3 md:py-4 px-2 md:px-4 font-bold text-xs md:text-base text-gray-900">
                    비교 항목
                  </th>
                  <th className="text-center py-3 md:py-4 px-2 md:px-4 font-bold text-xs md:text-base text-gray-700">
                    1세대
                  </th>
                  <th className="text-center py-3 md:py-4 px-2 md:px-4 font-bold text-xs md:text-base text-gray-700">
                    2세대
                  </th>
                  <th className="text-center py-3 md:py-4 px-2 md:px-4 font-bold text-xs md:text-base text-teal-700">
                    3세대
                  </th>
                </tr>
              </thead>
              <tbody className="text-xs md:text-sm">
                <tr className="border-b border-teal-200">
                  <td className="py-3 md:py-4 px-2 md:px-4 font-semibold text-gray-900">
                    원격 검침
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-close-line text-xl md:text-2xl text-red-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-subtract-line text-xl md:text-2xl text-yellow-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-check-line text-xl md:text-2xl text-teal-600"></i>
                  </td>
                </tr>
                <tr className="border-b border-teal-200">
                  <td className="py-3 md:py-4 px-2 md:px-4 font-semibold text-gray-900">
                    부정사용 방지
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-close-line text-xl md:text-2xl text-red-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-subtract-line text-xl md:text-2xl text-yellow-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-check-line text-xl md:text-2xl text-teal-600"></i>
                  </td>
                </tr>
                <tr className="border-b border-teal-200">
                  <td className="py-3 md:py-4 px-2 md:px-4 font-semibold text-gray-900">
                    납 성분 제거
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-close-line text-xl md:text-2xl text-red-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-close-line text-xl md:text-2xl text-red-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-check-line text-xl md:text-2xl text-teal-600"></i>
                  </td>
                </tr>
                <tr className="border-b border-teal-200">
                  <td className="py-3 md:py-4 px-2 md:px-4 font-semibold text-gray-900">
                    IoT 연동
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-close-line text-xl md:text-2xl text-red-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-close-line text-xl md:text-2xl text-red-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-check-line text-xl md:text-2xl text-teal-600"></i>
                  </td>
                </tr>
                <tr>
                  <td className="py-3 md:py-4 px-2 md:px-4 font-semibold text-gray-900">
                    실시간 모니터링
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-close-line text-xl md:text-2xl text-red-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-close-line text-xl md:text-2xl text-red-600"></i>
                  </td>
                  <td className="text-center py-3 md:py-4 px-2 md:px-4">
                    <i className="ri-check-line text-xl md:text-2xl text-teal-600"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
